import React, { useEffect, useState, useRef, useMemo } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import { printTableElement } from '../../utils/exportUtils';
import { downloadAttachment, dataUrlToBlobUrl } from '../../utils/attachmentStorage';
import { adminAPI, raBillsAPI } from '../../services/authService';
import { formatDateIN, getProjectElapsedPercent, getProjectConstructionDays, stripBrackets } from '../../utils/dateFormat';
import ProjectNameText from '../../components/ProjectNameText';

const formatCurrency = (val) => {
  if (!val && val !== 0) return '₹0';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val)}`;
};

const formatBuiltUpArea = (sqft) => {
  if (!sqft && sqft !== 0) return '-';
  return new Intl.NumberFormat('en-IN').format(sqft) + ' sq ft';
};

const getScheduleStatus = (start, end) => {
  const days = getProjectConstructionDays(start, end);
  if (!days) return { label: '-', color: '#6b7280' };
  const pct = getProjectElapsedPercent(start, end);
  const toLocal = (v) => {
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'string') {
      const part = v.trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
        const [y, m, d] = part.split('-').map(Number);
        return new Date(y, m - 1, d).getTime();
      }
      return new Date(v).getTime();
    }
    return null;
  };
  const e = toLocal(end);
  const now = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime();
  if (now > e) return { label: 'OVERDUE', color: '#b91c1c' };
  if (pct >= 90) return { label: 'NEAR DEADLINE', color: '#b45309' };
  return { label: 'ON SCHEDULE', color: '#047857' };
};

const getComplianceBadge = (status) => {
  const s = String(status || '').toLowerCase();
  const isApproved = s.includes('approve') || s === 'yes' || s === 'ok' || s === 'cleared';
  return { approved: isApproved, label: status || '-' };
};

const ProjectScope = ({ initialProjectId, onClearProject, onBackToOverview }) => {
  const [dataReady, setDataReady] = useState(false);
  const [selectedProject, setSelectedProject] = useState(initialProjectId ? String(initialProjectId) : 'all');
  const [projects, setProjects] = useState([]);
  const [projectDetails, setProjectDetails] = useState({});
  const [raBills, setRaBills] = useState([]);
  const contentRef = useRef(null);

  const handleViewAttachment = async (projectId, att) => {
    if (att.data_url) {
      const blobUrl = dataUrlToBlobUrl(att.data_url);
      if (blobUrl) {
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else {
        downloadAttachment(att, `Attachment: ${att.name}`);
      }
      return;
    }
    if (att.path || att.filename) {
      try {
        const res = await adminAPI.fetchScopeAttachment(projectId, att.filename || att.path?.split(/[/\\]/).pop());
        const blob = res?.data;
        if (blob) {
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
      } catch (e) {
        console.warn('View failed:', e);
      }
    }
  };

  const handleDownloadScopeAttachment = async (projectId, att) => {
    if (att.data_url) {
      downloadAttachment(att, `Attachment: ${att.name}`);
      return;
    }
    if (att.path || att.filename) {
      try {
        const res = await adminAPI.fetchScopeAttachment(projectId, att.filename || att.path?.split(/[/\\]/).pop(), true);
        const blob = res?.data;
        if (blob) {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = att.name || 'attachment';
          a.click();
          URL.revokeObjectURL(a.href);
        }
      } catch (e) {
        console.warn('Download failed:', e);
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsResp, raResp] = await Promise.all([
          adminAPI.getProjects(),
          raBillsAPI.getAll({ page: 1, pageSize: 500 }),
        ]);
        const response = projectsResp;
        const { rows } = unwrapListResponse(response);
        const list = safeArray(rows).map((row) => {
          const scope = row?.metadata?.scope || {};
          const meta = row?.metadata || {};
          return {
            id: row.id,
            name: row.name,
            agreement_prefix: row.agreementPrefix || row.agreement_prefix || row.code || `PRJ-${row.id}`,
            status: row.status || 'Active',
            location: row.location || meta.location || '',
            project_type: meta.project_type || meta.projectType || '',
            startDate: row.startDate,
            endDate: row.endDate,
            targetMandays: row.targetMandays ?? meta.time_allowed_maintenance ?? meta.timeAllowedMaintenance,
            letter_date: meta.letter_no ? (meta.letter_date ?? meta.letterOfAward?.letterDate ?? '') : '',
            letter_no: meta.letter_no ?? meta.letterOfAward?.letterNo ?? '',
            site_location_link: meta.site_location_link ?? meta.siteLocationLink ?? '',
            work_description: scope.work_description || '',
            agreement_value: Number(scope.agreement_value ?? 0),
            revised_value: Number(scope.revised_value ?? 0),
            builtup_area_sqft: scope.builtup_area_sqft ?? null,
            plot_area_acres: scope.plot_area_acres ?? null,
            floor_count: scope.floor_count ?? null,
            basement_levels: scope.basement_levels ?? null,
            key_structures: scope.key_structures || '',
            soil_type: scope.soil_type || '',
            fire_noc_status: scope.fire_noc_status || '',
            environmental_clearance: scope.environmental_clearance || '',
            scope_attachments: Array.isArray(scope.scope_attachments) ? scope.scope_attachments : [],
          };
        });
        list.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setProjects(list);
        const details = {};
        for (const p of list) {
          if (p.work_description || p.agreement_value > 0) details[p.id] = p;
        }
        setProjectDetails(details);
        const { rows: raRows } = unwrapListResponse(raResp);
        const bills = safeArray(raRows).map((r) => {
          const d = r?.data || r;
          return {
            project_id: d.project_id ?? r.project_id ?? r.projectId,
            upto_date_gross_work_done: Number(d.upto_date_gross_work_done ?? r.upto_date_gross_work_done ?? r.gross_work_done ?? 0),
          };
        });
        setRaBills(bills);
      } catch (error) {
        console.warn('Project scope API data unavailable:', error?.message || error);
        setProjects([]);
        setProjectDetails({});
        setRaBills([]);
      } finally {
        setDataReady(true);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (initialProjectId) setSelectedProject(String(initialProjectId));
  }, [initialProjectId]);

  const filteredProjects = selectedProject === 'all'
    ? projects.filter((p) => projectDetails[p.id])
    : projects.filter((p) => String(p.id) === selectedProject);

  const detailProject = selectedProject !== 'all' && filteredProjects.length > 0 ? filteredProjects[0] : null;

  const projectIdToUptoDateGross = useMemo(() => {
    const map = {};
    safeArray(raBills).forEach((b) => {
      const pid = Number(b.project_id);
      if (!pid) return;
      const gross = b.upto_date_gross_work_done || 0;
      map[pid] = Math.max(map[pid] || 0, gross);
    });
    return map;
  }, [raBills]);

  const getFinancialProgressPct = (project) => {
    const agreementVal = Number(project?.agreement_value ?? 0);
    if (!agreementVal) return 0;
    const uptoDateGross = projectIdToUptoDateGross[Number(project?.id)] || 0;
    return Math.min(100, Math.round((uptoDateGross / agreementVal) * 100));
  };

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Project Scope</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const renderProjectCard = (project) => {
    const financialPct = getFinancialProgressPct(project);
    const formatCurr = (v) => formatCurrency(v);
    return (
      <div
        key={project.id}
        role="button"
        tabIndex={0}
        onClick={() => setSelectedProject(String(project.id))}
        onKeyDown={(e) => e.key === 'Enter' && setSelectedProject(String(project.id))}
        style={{
          background: 'white',
          borderRadius: 8,
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)')}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)')}
      >
        <div style={{ height: 4, background: 'var(--kpi-accent-purple, #7C3AED)' }} />
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 8, background: '#EDE9FE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
              </div>
              <div>
                <h4 style={{ fontSize: 15, fontWeight: 600, color: '#1f2937', margin: 0 }}>
                  <ProjectNameText as="span">{stripBrackets(project.name)}</ProjectNameText>
                </h4>
                <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0 0' }}>{project.agreement_prefix}</p>
              </div>
            </div>
            <span style={{ background: project.status === 'Active' ? '#dcfce7' : '#f3f4f6', color: project.status === 'Active' ? '#166534' : '#6b7280', fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 6 }}>{project.status}</span>
          </div>
          <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginBottom: 12, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
            {stripBrackets(project.work_description || 'No description')}
          </p>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <div>
              <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>AGREEMENT VALUE</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', margin: 0 }}>{formatCurr(project.agreement_value)}</p>
            </div>
            <div>
              <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 2px 0' }}>END DATE</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', margin: 0 }}>{project.endDate ? formatDateIN(project.endDate) : '-'}</p>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1, marginRight: 8 }}>
              <p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 4px 0' }}>Financial Progress</p>
              <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, financialPct)}%`, height: '100%', background: '#7C3AED', borderRadius: 3 }} />
              </div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginLeft: 8 }}>{financialPct}%</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ width: 18, height: 18, flexShrink: 0, marginLeft: 4 }}><polyline points="9 18 15 12 9 6" /></svg>
          </div>
        </div>
      </div>
    );
  };

  const renderProjectDetail = (p) => {
    const constructionDays = getProjectConstructionDays(p.startDate, p.endDate) ?? p.targetMandays ?? '-';
    const elapsedPct = getProjectElapsedPercent(p.startDate, p.endDate);
    const financialPct = getFinancialProgressPct(p);
    const scheduleStatus = getScheduleStatus(p.startDate, p.endDate);
    const envBadge = getComplianceBadge(p.environmental_clearance);
    const fireBadge = getComplianceBadge(p.fire_noc_status);

    return (
      <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 20 }} ref={contentRef}>
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', padding: 24 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', margin: '0 0 8px 0' }}>
                <ProjectNameText as="span">{stripBrackets(p.name)}</ProjectNameText>
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {p.location && (
                  <span style={{ fontSize: 13, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                    {p.location}
                  </span>
                )}
                {p.project_type && (
                  <span style={{ background: '#f3f4f6', color: '#374151', fontSize: 12, fontWeight: 500, padding: '4px 10px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
                    {p.project_type}
                  </span>
                )}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 4px 0' }}>AGREEMENT VALUE</p>
              <p style={{ fontSize: 28, fontWeight: 700, color: '#7C3AED', margin: 0 }}>{formatCurrency(p.agreement_value)}</p>
            </div>
          </div>
          <div>
            <p style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px 0' }}>SCOPE</p>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.65, margin: 0 }}>{stripBrackets(p.work_description || '—')}</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
          <div className="kpi-card" style={{ borderLeft: '4px solid var(--kpi-accent-purple, #7C3AED)' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px 0', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
              TIMELINE & CONTRACTUAL
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: '#6b7280' }}>LOA DATE</span><span style={{ fontWeight: 500 }}>{p.letter_date ? formatDateIN(p.letter_date) : '—'}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: '#6b7280' }}>COMMENCEMENT</span><span style={{ fontWeight: 500 }}>{p.startDate ? formatDateIN(p.startDate) : '—'}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '8px 10px', background: '#F5F3FF', borderRadius: 8, margin: '0 -4px' }}><span style={{ color: '#6D28D9', fontWeight: 600 }}>TARGET END</span><span style={{ fontWeight: 600, color: '#6D28D9' }}>{p.endDate ? formatDateIN(p.endDate) : '—'}</span></div>
            </div>
            <div style={{ marginTop: 20, textAlign: 'center', padding: 16, background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: 36, fontWeight: 700, color: '#1f2937' }}>{constructionDays}</div>
              <p style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, margin: '4px 0 0 0' }}>CONSTRUCTION DAYS ALLOWED</p>
            </div>
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>FINANCIAL PROGRESS</span><span style={{ fontSize: 14, fontWeight: 600 }}>{financialPct}%</span></div>
              <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${financialPct}%`, height: '100%', background: '#7C3AED', borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>PROJECT ELAPSED TIME</span><span style={{ fontSize: 14, fontWeight: 600 }}>{elapsedPct}%</span></div>
                <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${elapsedPct}%`, height: '100%', background: '#9ca3af', borderRadius: 4, transition: 'width 0.3s' }} />
                </div>
                <p style={{ fontSize: 12, fontWeight: 600, color: scheduleStatus.color, margin: '8px 0 0 0' }}>{scheduleStatus.label}</p>
              </div>
            </div>
          </div>

          <div className="kpi-card" style={{ borderLeft: '4px solid var(--kpi-accent-orange, #f59e0b)' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px 0', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
              COMPLIANCE STATUS
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: envBadge.approved ? '#dcfce7' : '#fef3c7', borderRadius: 8 }}>
                {envBadge.approved ? <svg viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" style={{ width: 20, height: 20, flexShrink: 0 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg> : <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" style={{ width: 20, height: 20, flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>}
                <span style={{ fontWeight: 600, color: envBadge.approved ? '#166534' : '#b45309' }}>ENVIRONMENT {envBadge.approved ? 'Approved' : 'Pending'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: fireBadge.approved ? '#dcfce7' : '#fef3c7', borderRadius: 8 }}>
                {fireBadge.approved ? <svg viewBox="0 0 24 24" fill="none" stroke="#166534" strokeWidth="2" style={{ width: 20, height: 20, flexShrink: 0 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg> : <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" style={{ width: 20, height: 20, flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>}
                <span style={{ fontWeight: 600, color: fireBadge.approved ? '#166534' : '#b45309' }}>FIRE NOC {fireBadge.approved ? 'Approved' : 'Pending'}</span>
              </div>
              <div style={{ padding: 12, background: '#f3f4f6', borderRadius: 8 }}>
                <p style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', margin: '0 0 4px 0' }}>SOIL TYPE</p>
                <p style={{ fontSize: 14, fontWeight: 500, color: '#374151', margin: 0 }}>{p.soil_type || '—'}</p>
              </div>
            </div>
          </div>

          <div className="kpi-card" style={{ borderLeft: '4px solid var(--kpi-accent-purple, #8b5cf6)' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px 0', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
              TECHNICAL SPECIFICATIONS
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'BUILT-UP AREA', value: formatBuiltUpArea(p.builtup_area_sqft) },
                { label: 'PLOT AREA', value: p.plot_area_acres != null ? `${Number(p.plot_area_acres).toLocaleString('en-IN')} acres` : '—' },
                { label: 'FLOORS', value: p.floor_count != null ? String(p.floor_count) : '—' },
                { label: 'BASEMENTS', value: p.basement_levels != null ? String(p.basement_levels) : '—' },
              ].map((item, i) => (
                <li key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                  <span style={{ color: '#6b7280' }}>{item.label}</span>
                  <span style={{ fontWeight: 600, color: '#1f2937' }}>{item.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {p.site_location_link && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <a
              href={p.site_location_link.startsWith('http') ? p.site_location_link : `https://${p.site_location_link}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
              Site Location Link
            </a>
          </div>
        )}

        {(p.scope_attachments || []).length > 0 && (
          <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, border: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px 0' }}>Scope / Agreement Attachments</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {p.scope_attachments.map((file) => (
                <div key={file.name} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#374151' }}>{file.name}</span>
                  <button className="btn-secondary btn-sm" onClick={() => handleViewAttachment(p.id, file)}>View</button>
                  <button className="btn-secondary btn-sm" onClick={() => handleDownloadScopeAttachment(p.id, file)}>Download</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {selectedProject !== 'all' && (
            <button
              className="btn-secondary btn-sm"
              onClick={() => {
                setSelectedProject('all');
                if (typeof onBackToOverview === 'function') {
                  onBackToOverview();
                } else if (typeof onClearProject === 'function') {
                  onClearProject();
                }
              }}
            >
              ← Back to Overview
            </button>
          )}
          {selectedProject !== 'all' && (
            <button className="btn-secondary btn-sm" onClick={() => setSelectedProject('all')}>
              ← All Projects
            </button>
          )}
          <h2>Project Scope <span className="count-badge">{filteredProjects.length}</span></h2>
        </div>
        {detailProject && (
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Project Scope', subtitle: detailProject.name, element: contentRef.current })}
          >
            Download PDF
          </button>
        )}
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={selectedProject}
          onChange={setSelectedProject}
          projects={projects.map((p) => ({ id: p.id, name: p.name, agreement_prefix: p.agreement_prefix, status: p.status }))}
        />
        <ResetFiltersButton
          onClick={() => setSelectedProject(initialProjectId ? String(initialProjectId) : 'all')}
        />
      </div>

      {filteredProjects.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', background: '#f9fafb', borderRadius: 8, border: '1px dashed #e5e7eb' }}>
          <p style={{ fontSize: 15, color: '#6b7280', margin: 0 }}>
            No project scopes found. Create projects with scope details in <strong>Admin Panel → Projects</strong>.
          </p>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: '8px 0 0 0' }}>
            When adding or editing a project, fill in the scope section (Work Description, Agreement Amount / Tendered value, Key Structures, etc.) and save.
          </p>
        </div>
      )}

      {selectedProject === 'all' && filteredProjects.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
          {filteredProjects.map((p) => renderProjectCard(p))}
        </div>
      )}

      {detailProject && renderProjectDetail(detailProject)}

    </div>
  );
};

export default ProjectScope;
